import { useState, useEffect, useCallback } from "react";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import { TypedQuery, TypedVariables } from "components/templates/dataGridTable/serverSide/types";
import { useRecoilState } from "recoil";
import { merge } from "ts-deepmerge";
import { issueDataGridQueryState } from "./recoil";
import useFilterBuilder from "./useFilterBuilder";

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay ?? 100);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function useCustomFilterbar<E extends keyof EntityMappings>(query: TypedQuery<E>) {
  const [variables, setVariables] = useState<TypedVariables<E>>();
  const [filterState, setFilterState] = useRecoilState<TypedVariables<"Issue">>(issueDataGridQueryState);
  const { filters } = useFilterBuilder();
  const debouncedValue = useDebounce<TypedVariables<E> | undefined>(variables, 100);

  useEffect(() => {
    setVariables(merge(filterState, { where: { and: [filters] } }) as TypedVariables<E>);
  }, [filters, filterState, setVariables]);

  useEffect(() => {
    if (debouncedValue) {
      query({
        variables: debouncedValue,
      });
    }
  }, [query, debouncedValue]);

  const mockedQuery: TypedQuery<"Issue"> = useCallback(
    (options) => {
      setFilterState({
        where: options?.variables?.where,
        skip: options?.variables?.skip,
        order: options?.variables?.order,
        take: options?.variables?.take,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query]
  );
  return mockedQuery;
}

export default useCustomFilterbar;
